/**
 * WALL media-zone feed selection (#188; curation per design-council PART X
 * Ruling 4, #269).
 *
 * The wall's media banks surface live feeds. The DEFAULT is ALERT-DRIVEN: for
 * the top urgent alerts (by Response Level), surface the nearest camera so the
 * wall auto-frames "the feed that matters right now" without an operator
 * touching it — but SEATING PREFERS CURRENTLY-BROADCASTING feeds (Ruling 4):
 * candidates rank live-first (the #256 classification axes + the #232 runtime
 * honesty signal via `deadIds`), THEN alert-proximity among the live. A
 * non-broadcasting feed seats only when no broadcasting candidate remains
 * unseated; a runtime-dead feed (honest OFFLINE placard) seats only when
 * nothing showing pictures qualifies — a 50% dead wall while live pictures
 * exist is a curation failure (wall-QA round 1).
 *
 * Operator intent is EXEMPT from curation: an escalated camera (#222) and
 * pinned feeds (#182/#216) seat as directed — curation governs the AUTO tiers.
 *
 * Pure + deterministic (no React / fetch / Date.now) so the selection is
 * unit-testable; the React zone (`WallMediaZone`) just feeds it fetched data.
 */

export interface WallAlertAnchor {
  /** `source:id` queue id (dedupe-stable). */
  id: string;
  /** Short hazard label for the caption, e.g. "High-Risk Hotspot". */
  label: string;
  /** Sort priority — Response Level (higher = more urgent). */
  rank: number;
  /** ISO recency (tiebreak within a rank). */
  timeIso: string;
  lat: number;
  lon: number;
}

export interface WallCameraCandidate {
  id: string;
  name: string;
  lat: number;
  lon: number;
  /** Catalog-live (`status === 'live'`). Only catalog-live cameras are ever
   *  surfaced on the wall — a catalog-dead feed is never framed. */
  live: boolean;
  /** CURRENTLY-BROADCASTING by the #256 classification axes — a real mounted
   *  stream (`classifyWallMedia(...) === 'live'`: status live + stream_url,
   *  not an mp4 clip). Snapshot/clip cameras are catalog-live but NOT
   *  broadcasting; Ruling 4 seats them only after broadcasting candidates.
   *  Optional (default false) so older callers/fixtures stay valid. */
  broadcast?: boolean;
  /** WALL-CURATED (#273, PART XI Ruling 5.3): the operator featured this camera
   *  for the wall's ambient pool. AMBIENT seats draw featured-first (live-first
   *  within the pool) — featured never overrides alert-driven seating, only the
   *  no-alert-demand cycle tiers. Optional (default false). */
  featured?: boolean;
  /** A FRESH (≤30 min) featured SNAPSHOT — an AB fire-tower-class still seats
   *  with an honest SNAPSHOT label even though it isn't broadcasting, capped per
   *  flank bank (Ruling 5.5). Since #382 freshness is a PREFERENCE, not a GATE:
   *  a fresh featured still is preferred first for a curated seat, but a STALE
   *  featured still (this false) no longer vacates — an explicitly-featured cam
   *  the operator chose still seats (below the fresh ones, sharing the same
   *  per-bank stills budget). Optional (default false). */
  freshSnapshot?: boolean;
}

export interface WallPin {
  /** A pinned camera, an ambient news outlet (#182), or an ambient drone feed
   *  (#216). News/drone pins ride the same wall mosaic but resolve via their own
   *  YouTube-backed path, not a camera lookup. */
  kind: 'camera' | 'news' | 'drone';
  id: string;
  label: string;
}

export type WallFeedReason = 'escalated' | 'alert' | 'pinned' | 'cycle';

/** A curated YouTube-live NEWS outlet used to fill REMAINING empty wall slots
 *  (#334/#362) once the live-camera tiers are exhausted — never instead of a
 *  broadcasting camera. `id` is a NEWS_SOURCES slug (resolved to the outlet's
 *  current live by the same `WallNewsTile → NewsMedia` path a pinned news feed
 *  uses); `label` is the tile caption. */
export interface WallNewsFallbackItem {
  id: string;
  label: string;
}

export interface WallFeed {
  /** A 'drone' feed only arises from a pinned ambient feed (#216). A 'news' feed
   *  arises either from a pinned ambient outlet (#182) OR — since #334/#362 — from
   *  the NEWS-FALLBACK tier (reason='cycle'), which auto-seats curated YouTube-live
   *  news into otherwise-empty slots once the broadcasting cameras are exhausted.
   *  Every alert/escalated feed is still a camera. */
  kind: 'camera' | 'news' | 'drone';
  id: string;
  /** Camera display name (tile caption primary line). */
  label: string;
  /** Why this feed is on the wall — drives the caption's context suffix. */
  reason: WallFeedReason;
  /** Hazard label of the alert that surfaced it (reason='alert') or the escalated
   *  target's label (reason='escalated'). */
  nearAlert?: string;
}

/** An operator-escalated target (#222) — surfaced FIRST on the wall, ahead of the
 *  alert-driven default. Either a specific camera (frame that exact feed) or a
 *  point (frame the nearest live cameras to it). */
export interface WallEscalatedTarget {
  /** When the target IS a camera, force this feed first (if live + known). */
  cameraId?: string;
  /** Point to surface nearest live cameras to (camera or incident target). */
  lat?: number;
  lon?: number;
  /** Caption label for the escalated feed(s). */
  label: string;
}

const EARTH_RADIUS_KM = 6371;
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/** Great-circle distance in km (haversine). */
export function haversineKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Alerts sorted the queue way: Response Level desc, then recency desc. */
function sortAnchors(anchors: WallAlertAnchor[]): WallAlertAnchor[] {
  return [...anchors].sort((a, b) => {
    if (b.rank !== a.rank) return b.rank - a.rank;
    return (Date.parse(b.timeIso) || 0) - (Date.parse(a.timeIso) || 0);
  });
}

/** Nearest eligible, not-yet-used camera within `maxKm` of a point, or null. */
function nearestCamera(
  lat: number,
  lon: number,
  cameras: WallCameraCandidate[],
  used: Set<string>,
  maxKm: number,
  eligible: (c: WallCameraCandidate) => boolean,
): WallCameraCandidate | null {
  let best: WallCameraCandidate | null = null;
  let bestKm = Infinity;
  for (const c of cameras) {
    if (!eligible(c) || used.has(c.id)) continue;
    const km = haversineKm(lat, lon, c.lat, c.lon);
    if (km < bestKm && km <= maxKm) {
      best = c;
      bestKm = km;
    }
  }
  return best;
}

export interface SelectWallFeedsOpts {
  alerts: WallAlertAnchor[];
  cameras: WallCameraCandidate[];
  pins: WallPin[];
  /** Tiles to fill. */
  slots: number;
  /** Radius cap for an alert→camera match (km). Beyond it, no camera is framed
   *  for that alert and the slot falls through to the later tiers. */
  maxKm?: number;
  /** Operator escalation (#222) — surfaced FIRST, ahead of the alert default. */
  escalated?: WallEscalatedTarget;
  /** Feeds whose mounted players recently reported NOT showing pictures
   *  (offline / stalled-over-black) — the #232 runtime-honesty signal from
   *  `wallFeedHealth`. Demoted to the last-resort tier (Ruling 4): the next
   *  seating pass replaces a died tile instead of re-seating it. */
  deadIds?: ReadonlySet<string>;
  /** Cap on curated featured-STILL seats PER FLANK BANK — the bounded stills
   *  exception (Ruling 5.5: "1 per flank bank"). A flank bank can't stack two
   *  curated static snapshots; the cap distributes them across banks so the
   *  wall stays motion-forward. Default 1. Banks are `bankSlots`-sized output
   *  regions (see `bankSlots`); without it the whole selection is one bank.
   *
   *  This SAME per-bank budget also bounds the #382 featured GUARANTEE (an
   *  explicitly-featured non-broadcasting cam is seated ahead of the general
   *  broadcasting flood so it can't be invisible on a populated wall) — the
   *  guarantee FLOOR and this ceiling share one budget, so a bank never shows
   *  more than `cap` curated stills whether they arrive by guarantee or fill. */
  maxFeaturedStills?: number;
  /** Per-flank-bank size (output slots) used to scope `maxFeaturedStills`. The
   *  wall's flank banks each slice a `bankSlots`-wide region of this ranking
   *  (`WallMediaZone` passes its reel-pool depth), so the stills cap can be
   *  enforced per bank rather than wall-wide. Omitted → one bank (global cap). */
  bankSlots?: number;
  /** Curated YouTube-live NEWS outlets that fill REMAINING empty slots after the
   *  broadcasting cameras are seated (#334/#362) — a blank then shows a live
   *  news tile instead of "Awaiting feed…". Seated in list order, each at most
   *  once, deduped against pinned news; news NEVER displaces a broadcasting
   *  camera (the tier runs after the broadcasting cycle) and the picture/dead
   *  camera tiers fill whatever news leaves. Omitted / empty → no auto news (the
   *  pre-#362 behaviour: news surfaces only when an operator pins it). */
  newsFallback?: WallNewsFallbackItem[];
}

/**
 * Choose the wall's feeds (PART X Ruling 4 seating order):
 *
 *   0) ESCALATED (#222) — operator intent, exempt from curation.
 *   1) ALERT-driven among the BROADCASTING — nearest currently-broadcasting
 *      camera to each top alert. ALERT demand overrides curation (Ruling 5.3).
 *   2) PINNED (#182/#216) — operator-curated, exempt from curation.
 *   3) AMBIENT (no alert demand) — FEATURED-first (Ruling 5.3): featured
 *      broadcasting, then the general broadcasting pool CAPPED to leave a
 *      bounded RESERVATION (#382 — `maxFeaturedStills` per flank bank) so an
 *      operator's featured NON-broadcasting cam can't be starved off the wall by
 *      a broadcasting flood. The reserved slots are filled in tier 4 (below the
 *      alert-anchored pictures, so a featured cam never preempts an alert).
 *   3.5) NEWS FALLBACK (#334/#362) — curated YouTube-live news fills REMAINING
 *      empty slots so a quiet/short pool shows live news instead of a blank.
 *      After every broadcasting camera (news never displaces a live camera);
 *      before the picture/dead tiers (live news reads better than a frozen
 *      still). Opt-in via `newsFallback`; omitted → news only when pinned.
 *   4) PICTURE-BEARING non-broadcast (snapshot / clip): alert-anchored, then the
 *      BOUNDED featured fresh-stills exception (Ruling 5.5), then the general
 *      picture cycle — honest stills, seated only once broadcasting candidates
 *      are exhausted.
 *   5) LAST RESORT — runtime-dead feeds (honest OFFLINE placards), only when
 *      nothing showing pictures qualifies.
 *
 * Capped at `slots`, deduped by camera id. Catalog-dead cameras never seat.
 */
export function selectWallFeeds(opts: SelectWallFeedsOpts): WallFeed[] {
  const { alerts, cameras, pins, slots, escalated } = opts;
  const maxKm = opts.maxKm ?? 250;
  const dead = opts.deadIds ?? new Set<string>();
  const maxFeaturedStills = opts.maxFeaturedStills ?? 1;
  const bankSlots = opts.bankSlots ?? null;
  const newsFallback = opts.newsFallback ?? [];
  const used = new Set<string>();
  const feeds: WallFeed[] = [];
  const camById = new Map(cameras.map((c) => [c.id, c]));

  // Liveness classes (Ruling 4): broadcasting > picture-bearing > runtime-dead.
  const isBroadcasting = (c: WallCameraCandidate): boolean =>
    c.live && c.broadcast === true && !dead.has(c.id);
  const isPicture = (c: WallCameraCandidate): boolean =>
    c.live && c.broadcast !== true && !dead.has(c.id);
  const isDemoted = (c: WallCameraCandidate): boolean => c.live && dead.has(c.id);
  // Curation classes (Ruling 5.3/5.5): a quiet wall draws FEATURED-first.
  const isFeaturedBroadcasting = (c: WallCameraCandidate): boolean =>
    c.featured === true && isBroadcasting(c);
  // An explicitly-featured NON-broadcasting live camera (511 / scenic vista —
  // the operator's curated ambient pool). The curated-stills class (#382):
  // freshness is now a PREFERENCE (see `isFeaturedFreshStill`), NOT a gate — an
  // operator-chosen feature seats even without a ≤30 min snapshot, so a stale
  // featured cam no longer silently vanishes from the wall.
  const isFeaturedPicture = (c: WallCameraCandidate): boolean =>
    c.featured === true && isPicture(c);
  // The FRESH subset of the featured-picture class — preferred first for a
  // curated seat (a fresh capture reads better at wall distance than a stale
  // one), but a stale featured picture still seats via the relaxed class above.
  const isFeaturedFreshStill = (c: WallCameraCandidate): boolean =>
    isFeaturedPicture(c) && c.freshSnapshot === true;
  // Fresh-preferred pick within the featured-picture class (curated seat order).
  const pickFeaturedPicture = (): WallCameraCandidate | undefined =>
    cameras.find((c) => isFeaturedFreshStill(c) && !used.has(c.id)) ??
    cameras.find((c) => isFeaturedPicture(c) && !used.has(c.id));

  const seat = (cam: WallCameraCandidate, reason: WallFeedReason, nearAlert?: string): void => {
    used.add(cam.id);
    feeds.push({ kind: 'camera', id: cam.id, label: cam.name, reason, ...(nearAlert ? { nearAlert } : {}) });
  };

  /** Nearest camera of a class to each top alert (queue order). */
  const alertPass = (eligible: (c: WallCameraCandidate) => boolean): void => {
    for (const a of sortAnchors(alerts)) {
      if (feeds.length >= slots) break;
      const cam = nearestCamera(a.lat, a.lon, cameras, used, maxKm, eligible);
      if (!cam) continue;
      seat(cam, 'alert', a.label);
    }
  };

  /** Fill remaining slots from a class in list order, capped at `cap` seats and
   *  LEAVING `reserveSlots` slots unfilled — a soft reservation for a later,
   *  lower-in-list-but-must-stay-visible tier. The #382 featured-picture
   *  reservation caps the general broadcasting cycle this way so a broadcasting
   *  flood can't starve an operator's Feature: the reserved slots are filled in
   *  the tier-4 picture pass (below the alert-anchored pictures — an alert still
   *  wins its own seat — but above being crowded out entirely). */
  const cyclePass = (
    eligible: (c: WallCameraCandidate) => boolean,
    cap = Infinity,
    reserveSlots = 0,
  ): void => {
    const ceiling = slots - reserveSlots;
    let seated = 0;
    for (const c of cameras) {
      if (feeds.length >= ceiling || seated >= cap) break;
      if (!eligible(c) || used.has(c.id)) continue;
      seat(c, 'cycle');
      seated += 1;
    }
  };

  /** Fill REMAINING empty slots with curated YouTube-live NEWS (#334/#362) —
   *  reached only after the broadcasting cameras are seated, so a blank shows a
   *  live news tile instead of "Awaiting feed…". Each outlet seats at most once
   *  and is skipped if already used (dedupes against a pinned news feed of the
   *  same id), in list order, until slots fill or the curated set is exhausted.
   *  News never displaces a broadcasting camera (this runs after the cycle) and
   *  never repeats an outlet to pad — the picture/dead camera tiers fill the
   *  rest. */
  const newsFallbackPass = (reserveSlots = 0): void => {
    const ceiling = slots - reserveSlots;
    for (const item of newsFallback) {
      if (feeds.length >= ceiling) break;
      if (used.has(item.id)) continue;
      used.add(item.id);
      feeds.push({ kind: 'news', id: item.id, label: item.label, reason: 'cycle' });
    }
  };

  /** Picture-bearing fill (snapshot / clip): featured fresh stills seat with
   *  curated priority, but at most `cap` PER FLANK BANK (Ruling 5.5 — one bank
   *  can't stack two static snapshots). Banks tile this selection into
   *  `bankSlots`-sized output regions; the fill is slot-by-slot, so a bank that
   *  has hit its cap yields to a general (non-curated-still) picture while the
   *  next featured still is RESERVED for a later bank's exception seat. An
   *  over-cap featured still falls through as a general picture only once no
   *  plain picture remains (a curated snapshot beats an empty tile). Without
   *  `bankSlots` the whole selection is one bank (global cap). */
  const fillPictures = (cap: number, banks: number | null): void => {
    const stillSeatsInBank = new Map<number, number>();
    const bankOf = (idx: number): number =>
      banks && banks > 0 ? Math.floor(idx / banks) : 0;
    // Featured pictures ALREADY seated (by the #382 guarantee pass, or an
    // alert-adjacent pass) count against this per-bank ceiling, so the guarantee
    // FLOOR and the Ruling 5.5 CEILING share ONE budget — a bank never stacks
    // more than `cap` curated stills, and a guaranteed still is never re-seated.
    feeds.forEach((f, idx) => {
      const c = camById.get(f.id);
      if (c && isFeaturedPicture(c)) {
        const b = bankOf(idx);
        stillSeatsInBank.set(b, (stillSeatsInBank.get(b) ?? 0) + 1);
      }
    });
    const isGeneralPicture = (c: WallCameraCandidate): boolean =>
      isPicture(c) && !isFeaturedPicture(c);
    while (feeds.length < slots) {
      const bank = bankOf(feeds.length);
      if ((stillSeatsInBank.get(bank) ?? 0) < cap) {
        const still = pickFeaturedPicture();
        if (still) {
          seat(still, 'cycle');
          stillSeatsInBank.set(bank, (stillSeatsInBank.get(bank) ?? 0) + 1);
          continue;
        }
      }
      const pic =
        cameras.find((c) => isGeneralPicture(c) && !used.has(c.id)) ?? pickFeaturedPicture();
      if (!pic) break;
      seat(pic, 'cycle');
    }
  };

  // 0) ESCALATED — the operator promoted this target; surface it first. An
  //    exact camera target seats regardless of broadcast state (operator
  //    intent); a point target frames the nearest broadcasting camera, falling
  //    back to a picture-bearing one (never a runtime-dead placard).
  if (escalated && slots > 0) {
    let cam: WallCameraCandidate | null = null;
    if (escalated.cameraId) {
      const exact = cameras.find((c) => c.id === escalated.cameraId);
      if (exact && exact.live) cam = exact;
    }
    if (!cam && escalated.lat != null && escalated.lon != null) {
      cam =
        nearestCamera(escalated.lat, escalated.lon, cameras, used, maxKm, isBroadcasting) ??
        nearestCamera(escalated.lat, escalated.lon, cameras, used, maxKm, isPicture);
    }
    if (cam) seat(cam, 'escalated', escalated.label);
  }

  // 1) ALERT-driven among the broadcasting.
  alertPass(isBroadcasting);

  // 2) PINNED feeds (operator-curated, synced cross-window — exempt from
  //    curation). A news (#182) or drone (#216) pin rides the mosaic as an
  //    ambient feed — preserve its kind so the tile resolves via its
  //    YouTube-backed path; a camera pin keeps the camById name fallback.
  for (const p of pins) {
    if (feeds.length >= slots) break;
    if (used.has(p.id)) continue;
    used.add(p.id);
    feeds.push({
      kind: p.kind,
      id: p.id,
      label: p.label || (p.kind === 'camera' ? camById.get(p.id)?.name : undefined) || p.id,
      reason: 'pinned',
    });
  }

  // 3) AMBIENT among the live pool (no alert demand) — FEATURED-first (Ruling
  //    5.3: curated vistas/lookouts/municipal feeds seat ahead of the general
  //    live pool). Featured BROADCASTING (curated live) seats first; then the
  //    general broadcasting pool — but CAPPED to LEAVE a bounded RESERVATION so
  //    an operator's featured NON-broadcasting cam (#382) can't be starved off
  //    the wall by a broadcasting flood (the exact bug: on a populated `goc`
  //    wall the broadcasting cycle filled every slot, so the featured picture
  //    tier below was never reached). The reservation is `maxFeaturedStills` per
  //    flank bank (the Ruling 5.5 stills budget), so the wall stays motion-
  //    forward; it is released to the alert-anchored pictures first (an alert
  //    still wins its own seat — the featured cam NEVER preempts an alert) and
  //    then to the featured cams in the tier-4 picture fill.
  cyclePass(isFeaturedBroadcasting);
  // Reserve at most `maxFeaturedStills` slots — NOT × banks. The reserved slots
  // are the TAIL of the output, and each flank renders exactly ONE bank
  // (`bankWindow` slices its own `[poolOffset, poolOffset+poolSlots)`), whose
  // curated-still capacity is `maxFeaturedStills` per Ruling 5.5. Reserving more
  // would only pile surplus stills into that one bank (over-cap + starved
  // broadcasting) or leak the slot to a general snapshot — so one featured cam
  // is the honest per-flank guarantee (a 2nd would need two static tiles in the
  // rendered bank, which Ruling 5.5 forbids). A featured cam beyond the reserve
  // still competes best-effort in the picture fill below.
  const featuredWaiting = cameras.filter((c) => isFeaturedPicture(c) && !used.has(c.id)).length;
  const reservedForFeatured = Math.min(featuredWaiting, maxFeaturedStills);
  cyclePass(isBroadcasting, Infinity, reservedForFeatured);

  // 3.5) NEWS FALLBACK (#334/#362) — once every broadcasting camera is seated,
  //      fill any REMAINING empty slots with curated YouTube-live news so a
  //      quiet/short pool shows a live news tile instead of "Awaiting feed…".
  //      Runs BEFORE the picture/dead tiers (a live news video reads better at
  //      wall distance than a frozen snapshot or an OFFLINE placard) but AFTER
  //      all broadcasting cameras — news never displaces a live camera. It also
  //      leaves the #382 featured reservation so an operator's Feature (intent)
  //      isn't displaced by auto-news.
  newsFallbackPass(reservedForFeatured);

  // 4) Non-broadcasting but picture-bearing (snapshot / clip): alert-anchored
  //    pictures FIRST — an alert whose nearest camera is only a snapshot still
  //    wins its seat (the reservation is released to it, so a featured cam NEVER
  //    preempts an alert) — then the picture fill: the BOUNDED featured-still
  //    exception (Ruling 5.5 — curated stills seat with honest SNAPSHOT labels,
  //    capped PER FLANK BANK, fresh-preferred but no longer freshness-GATED
  //    since #382) interleaved with the general picture cycle. `fillPictures`
  //    seats the reserved featured cams here (below the alerts) and PRE-COUNTS
  //    every already-seated featured picture — including a pinned/escalated one
  //    — so a bank never stacks more than `cap` (no double-seat).
  alertPass(isPicture);
  fillPictures(maxFeaturedStills, bankSlots);

  // 5) LAST RESORT — runtime-dead feeds. Honest-OFFLINE only when nothing
  //    showing pictures qualifies (a dead placard beats a blank tile).
  cyclePass(isDemoted);

  return feeds.slice(0, slots);
}

/**
 * Window one flank BANK's slice of the shared `selectWallFeeds` ranking (#371).
 *
 * The wall's two media flanks share ONE ranking but show DISTINCT feeds: each
 * bank renders its `[poolOffset … poolOffset + poolSlots)` slice (disjoint, so
 * the same camera never appears on both flanks — wall-v2 QA). But when the
 * resolved pool is short — fewer total feeds than `poolOffset + poolSlots` — a
 * later bank's disjoint slice can be EMPTY or shorter than its on-screen page
 * (`visibleSlots`), so the flank renders "Awaiting feed…" / black tiles even
 * though live feeds exist on the OTHER flank. A dead tile while live feeds are
 * available is a curation failure (wall-QA round 1), so:
 *
 *   • If the disjoint slice can fill the visible page (`length ≥ visibleSlots`),
 *     return it unchanged — distinct flanks preserved (the rich-pool default).
 *   • Otherwise BACKFILL from the rest of the ranking (deduped by id, in rank
 *     order) up to `poolSlots`, so the flank fills its page from whatever live
 *     feeds exist — a duplicated live feed beats a blank tile. Distinct feeds
 *     stay a PREFERENCE (slice-first), never bought at the cost of a dead tile.
 *
 * Pure + data-independent: given a `selected` list with ≥ `visibleSlots` total
 * feeds, every bank's window has ≥ `visibleSlots` feeds, so no on-screen page is
 * blank while a live feed is available. Genuine scarcity (fewer total feeds than
 * a page) still under-fills — that is honest, not a seating bug.
 */
export function bankWindow(
  selected: readonly WallFeed[],
  poolOffset: number,
  poolSlots: number,
  visibleSlots: number,
): WallFeed[] {
  const primary = selected.slice(poolOffset, poolOffset + poolSlots);
  // The disjoint slice already fills the visible page → distinct flanks, done.
  if (primary.length >= visibleSlots) return primary;
  // Backfill (deduped) from the whole ranking so the flank never goes blank
  // while feeds exist; slice feeds first (distinct preference), then the rest.
  const window: WallFeed[] = [...primary];
  const seen = new Set(primary.map((f) => f.id));
  for (const f of selected) {
    if (window.length >= poolSlots) break;
    if (seen.has(f.id)) continue;
    seen.add(f.id);
    window.push(f);
  }
  return window;
}
